import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { formatIQD, formatDate } from "../lib/format";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Search } from "lucide-react";
import StatusBadge from "../components/StatusBadge";

export default function EmployeeBalances() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [onlyOut, setOnlyOut] = useState(false);
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("outstanding_desc");
  const nav = useNavigate();

  const load = () => {
    api.get("/balances", { params: { q, only_outstanding: onlyOut, status } }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [q, onlyOut, status]);

  const sorted = [...rows].sort((a, b) => {
    if (sort === "outstanding_asc") return a.outstanding - b.outstanding;
    if (sort === "outstanding_desc") return b.outstanding - a.outstanding;
    if (sort === "name") return a.name.localeCompare(b.name);
    return 0;
  });

  return (
    <div className="space-y-6" data-testid="balances-page">
      <div>
        <h1 className="heading text-2xl font-extrabold tracking-tight">Employee Balances</h1>
        <p className="text-sm text-muted-foreground">Live outstanding balance per employee, calculated from orders and payments.</p>
      </div>
      <Card className="p-4">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-56">
            <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input placeholder="Search employee" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="bal-search" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-56" data-testid="bal-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="outstanding_desc">Sort: Highest outstanding</SelectItem>
              <SelectItem value="outstanding_asc">Sort: Lowest outstanding</SelectItem>
              <SelectItem value="name">Sort: Name</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="bal-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={onlyOut} onCheckedChange={setOnlyOut} data-testid="bal-only-out" />
            Only with outstanding
          </label>
        </div>
      </Card>
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-3">Employee</th><th className="p-3">Status</th>
                <th className="p-3 text-right">Total Orders</th>
                <th className="p-3 text-right">Food Cost</th>
                <th className="p-3 text-right">Paid</th>
                <th className="p-3 text-right">Outstanding</th>
                <th className="p-3">Last Payment</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} className="border-t table-row-hover cursor-pointer" onClick={() => nav(`/employees/${r.id}`)} data-testid={`bal-row-${r.id}`}>
                  <td className="p-3 font-medium">{r.name}<div className="text-xs text-muted-foreground">{r.phone || r.email || ""}</div></td>
                  <td className="p-3"><StatusBadge status={r.status} /></td>
                  <td className="p-3 text-right mono">{r.total_orders}</td>
                  <td className="p-3 text-right mono">{formatIQD(r.total_cost)}</td>
                  <td className="p-3 text-right mono text-emerald-700">{formatIQD(r.total_paid)}</td>
                  <td className={`p-3 text-right mono font-semibold ${r.outstanding > 0 ? "text-rose-700" : "text-slate-500"}`}>{formatIQD(r.outstanding)}</td>
                  <td className="p-3 text-muted-foreground">{r.last_payment_date ? formatDate(r.last_payment_date) : "—"}</td>
                </tr>
              ))}
              {sorted.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No employees found.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
